import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Activity, ArrowRight, FileText } from "lucide-react";
import Layout from "../components/Layout";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { api } from "../lib/api";
import { errMsg } from "../lib/errors";

const PROGNOSIS_COLOR = {
  good: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  fair: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  poor: "text-orange-400 bg-orange-500/10 border-orange-500/30",
  questionable: "text-red-300 bg-red-500/10 border-red-500/30",
  hopeless: "text-red-400 bg-red-600/20 border-red-600/40 font-bold",
  excellent: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40",
};

export default function PerioProsthoDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/perio-prostho/${id}`)
      .then((r) => setDoc(r.data))
      .catch((e) => toast.error(errMsg(e, "Failed to load")))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Layout><div className="flex items-center gap-2 text-[var(--pa-text-muted)] text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div></Layout>;
  if (!doc) return <Layout><div className="text-sm text-[var(--pa-text-muted)]">Not found.</div></Layout>;

  const a = doc.analysis || {};

  return (
    <Layout>
      <div className="kicker">Phase 1 · Diagnostics</div>
      <h1 data-testid="perio-detail-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3">
        <Activity size={22} className="text-[var(--pa-gold)]" /> Perio-Prostho Plan
      </h1>
      <div className="mt-1 text-xs text-[var(--pa-text-dim)]">{new Date(doc.created_at).toLocaleString()}</div>

      <AiOutputDisclaimer className="mt-4" />

      <section className="mt-7 grid sm:grid-cols-4 gap-3" data-testid="perio-staging">
        <Box label="Stage"><span className="font-serif text-3xl text-[var(--pa-gold)]">{a.staging || "—"}</span></Box>
        <Box label="Grade"><span className="font-serif text-3xl text-[var(--pa-gold)]">{a.grading || "—"}</span></Box>
        <Box label="Stability"><span className={`font-semibold capitalize ${a.perio_stability === "stable" ? "text-emerald-400" : "text-red-400"}`}>{a.perio_stability || "—"}</span></Box>
        <Box label="Before prostho?">
          <span className={`font-semibold ${a.must_complete_before_prostho ? "text-amber-300" : "text-emerald-400"}`}>
            {a.must_complete_before_prostho ? "Perio FIRST" : "Cleared"}
          </span>
        </Box>
      </section>

      {a.staging_rationale && (
        <p className="mt-4 text-sm text-[var(--pa-text-muted)] leading-relaxed">{a.staging_rationale}</p>
      )}

      {a.tooth_prognosis?.length > 0 && (
        <Section title="Per-Tooth Prognosis (Kwok & Caton)">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="perio-prognosis">
            {a.tooth_prognosis.map((t, i) => (
              <div key={i} className={`rounded border px-3 py-2 text-xs ${PROGNOSIS_COLOR[t.prognosis] || "border-[var(--pa-border)] bg-[var(--pa-bg-2)]"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono font-semibold text-[var(--pa-text)]">#{t.tooth}</span>
                  <span className="uppercase tracking-wider text-[10px]">{t.prognosis}</span>
                </div>
                {t.rationale && <p className="mt-1 text-[11px] text-[var(--pa-text-muted)] leading-snug">{t.rationale}</p>}
                {(t.ferrule_mm != null) && (
                  <div className="mt-1 text-[10px] text-[var(--pa-text-dim)]">Ferrule {t.ferrule_mm}mm · {t.ferrule_risk} · abutment {t.abutment_suitable ? "✓" : "✗"}</div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {a.strategic_extractions?.length > 0 && (
        <Section title="Strategic Extractions">
          <div className="space-y-2" data-testid="perio-extractions">
            {a.strategic_extractions.map((x, i) => (
              <div key={i} className="bg-red-500/5 border border-red-500/30 rounded p-3 text-sm">
                <span className="font-mono font-semibold text-red-300">#{x.tooth}</span>
                <span className="ml-2 text-[var(--pa-text-muted)]">{x.reason}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {a.crown_lengthening_needed?.length > 0 && (
        <Section title="Crown Lengthening Indicated">
          <div className="space-y-2">
            {a.crown_lengthening_needed.map((x, i) => (
              <div key={i} className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-3 text-sm">
                <span className="font-mono font-semibold text-[var(--pa-gold)]">#{x.tooth}</span>
                <span className="ml-2 capitalize text-[var(--pa-text)]">{x.type}</span>
                {x.biologic_width_calc && <div className="text-xs text-[var(--pa-text-muted)] mt-1">{x.biologic_width_calc}</div>}
                {x.expected_ferrule_gain_mm != null && <div className="text-xs text-[var(--pa-text-dim)] mt-1">Expected ferrule gain: {x.expected_ferrule_gain_mm} mm</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {a.treatment_sequence?.length > 0 && (
        <Section title="Treatment Sequence">
          <div className="space-y-3" data-testid="perio-sequence">
            {a.treatment_sequence.map((p, i) => (
              <div key={i} className="border border-[var(--pa-border)] rounded p-4 bg-[var(--pa-bg-2)]">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[var(--pa-gold)] text-[var(--pa-bg)] text-xs font-bold flex items-center justify-center">{p.phase}</span>
                  <span className="font-semibold text-[var(--pa-text)]">{p.label}</span>
                </div>
                <ul className="mt-2 ml-8 text-sm text-[var(--pa-text-muted)] list-disc space-y-0.5">
                  {(p.procedures || []).map((pr, j) => <li key={j}>{pr}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {a.cdt_codes?.length > 0 && (
        <Section title="CDT Billing Codes">
          <div className="grid sm:grid-cols-2 gap-2">
            {a.cdt_codes.map((c, i) => (
              <div key={i} className="text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded px-3 py-2">
                <span className="text-[var(--pa-gold)] font-mono font-semibold">{c.code}</span>
                <span className="ml-2 text-[var(--pa-text-muted)]">{c.description}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {doc.patient_id && (
        <Link to={`/treatment-plan/new?patient_id=${doc.patient_id}`} data-testid="perio-to-tx-plan"
          className="mt-8 inline-flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2.5 rounded-md hover:bg-[var(--pa-gold-hover)] transition">
          <FileText size={14} /> Synthesize into Treatment Plan <ArrowRight size={14} />
        </Link>
      )}
    </Layout>
  );
}

function Section({ title, children }) {
  return <section className="mt-7"><div className="kicker mb-3">{title}</div>{children}</section>;
}
function Box({ label, children }) {
  return (
    <div className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-4 text-center">
      <div className="text-[10px] uppercase tracking-wider text-[var(--pa-text-dim)] mb-2">{label}</div>
      <div>{children}</div>
    </div>
  );
}
